import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /** @type {MutationObserver | null} */
  #submenuMutationObserver = null;

  /** @type {MutationObserver | null} */
  #layoutMutationObserver = null;

  /** @type {HTMLElement | null} 保存被修改的 row 容器，用于 clear */
  #layoutRow = null;

  /** @type {HTMLElement[]} 保存被修改的 grid 列元素，用于 clear */
  #layoutCols = [];

  /** @type {HTMLElement[]} 保存被修改的内部元素，用于 clear */
  #layoutInners = [];

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(() => {
      this.#preloadImages();
      this.#applyCenteredLogoLayout();
    });

    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);

    requestAnimationFrame(() => {
      this.#applyCenteredLogoLayout();
    });

    this.#watchHeaderReady();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
    this.#layoutMutationObserver?.disconnect();
    this.#layoutMutationObserver = null;
  }

  /** 等待关键元素出现后再执行布局（兜底 hydration 场景） */
  #watchHeaderReady() {
    const headerComp = this.headerComponent;
    if (!headerComp) return;

    this.#layoutMutationObserver = new MutationObserver(() => {
      const hasLogo  = headerComp.querySelector('.header__heading, .header__logo, [class*="header__heading"]');
      const hasIcons = headerComp.querySelector('.header__icons, [class*="header__icons"]');
      if (hasLogo && hasIcons) {
        this.#applyCenteredLogoLayout();
        this.#layoutMutationObserver?.disconnect();
        this.#layoutMutationObserver = null;
      }
    });

    this.#layoutMutationObserver.observe(headerComp, { childList: true, subtree: true });

    setTimeout(() => {
      this.#layoutMutationObserver?.disconnect();
      this.#layoutMutationObserver = null;
    }, 3000);
  }

  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
    this.#applyCenteredLogoLayout();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  // ─────────────────────────────────────────────────────────────────────────
  // 核心布局方法
  // ─────────────────────────────────────────────────────────────────────────

  #applyCenteredLogoLayout = () => {
    const headerComp = this.headerComponent;
    if (!headerComp) return;

    // 移动端：恢复默认
    if (window.innerWidth < 990) {
      this.#clearCenteredLogoLayout();
      return;
    }

    // ── 1. 找三个关键元素 ────────────────────────────────────────────────
    const logo = findFirst(headerComp, [
      '.header__heading',
      '[class*="header__heading"]',
      '.header__logo',
    ]);

    const menu =
      findFirst(headerComp, ['.header__inline-menu', '[class*="inline-menu"]']) ||
      this.parentElement;

    const icons = findFirst(headerComp, [
      '.header__icons',
      '[class*="header__icons"]',
      '.header__icon-list',
      '.header__actions',
    ]);

    if (!(logo instanceof HTMLElement) || !(menu instanceof HTMLElement) || !(icons instanceof HTMLElement)) return;

    // ── 2. 找三者的最近公共祖先（LCA）作为 grid row ───────────────────────
    const row = findLowestCommonAncestor(logo, menu, icons);
    if (!(row instanceof HTMLElement)) return;

    // ── 3. 找 row 的直接子元素（各自的列容器）────────────────────────────
    const logoCol  = getDirectChildContainer(row, logo);
    const menuCol  = getDirectChildContainer(row, menu);
    const iconsCol = getDirectChildContainer(row, icons);

    // ── 4. 把 row 变成三列 grid ───────────────────────────────────────────
    setImportantStyles(row, {
      display: 'grid',
      gridTemplateColumns: 'minmax(0,1fr) auto minmax(0,1fr)',
      alignItems: 'center',
      columnGap: '24px',
      width: '100%',
    });
    this.#layoutRow = row;

    // ── 5. 分配 grid 列 ───────────────────────────────────────────────────
    // 若三列容器各不相同：对容器赋列号
    // 若有重叠（同一容器）：退而求其次，直接对元素本身赋列号
    const colsAreDistinct =
      menuCol !== logoCol &&
      menuCol !== iconsCol &&
      logoCol !== iconsCol;

    if (colsAreDistinct) {
      if (menuCol  instanceof HTMLElement) setImportantStyles(menuCol,  { gridColumn: '1', justifySelf: 'start',  margin: '0' });
      if (logoCol  instanceof HTMLElement) setImportantStyles(logoCol,  { gridColumn: '2', justifySelf: 'center', margin: '0', textAlign: 'center' });
      if (iconsCol instanceof HTMLElement) setImportantStyles(iconsCol, { gridColumn: '3', justifySelf: 'end',    margin: '0' });
      this.#layoutCols = [menuCol, logoCol, iconsCol].filter(Boolean);
    } else {
      // 容器有重叠，直接对三个元素本身赋列号
      setImportantStyles(menu,  { gridColumn: '1', justifySelf: 'start',  margin: '0' });
      setImportantStyles(logo,  { gridColumn: '2', justifySelf: 'center', margin: '0', textAlign: 'center' });
      setImportantStyles(icons, { gridColumn: '3', justifySelf: 'end',    margin: '0' });
      this.#layoutCols = [menu, logo, icons];
    }

    // ── 6. 内部元素对齐 ───────────────────────────────────────────────────
    setImportantStyles(menu,  { display: 'flex', alignItems: 'center', justifyContent: 'flex-start' });
    setImportantStyles(icons, { display: 'flex', alignItems: 'center', justifyContent: 'flex-end'   });
    this.#layoutInners = [menu, icons];
  };

  /** 清除所有 inline 布局样式，恢复主题默认 */
  #clearCenteredLogoLayout = () => {
    if (this.#layoutRow) {
      clearStyles(this.#layoutRow, [
        'display', 'grid-template-columns', 'align-items', 'column-gap', 'width',
      ]);
      this.#layoutRow = null;
    }
    this.#layoutCols.forEach((el) => clearStyles(el, ['grid-column', 'justify-self', 'margin', 'text-align']));
    this.#layoutCols = [];
    this.#layoutInners.forEach((el) => clearStyles(el, ['display', 'align-items', 'justify-content']));
    this.#layoutInners = [];
  };

  // ─────────────────────────────────────────────────────────────────────────
  // 以下为原始逻辑，未作修改
  // ─────────────────────────────────────────────────────────────────────────

  /** @type {State} */
  #state = { activeItem: null };

  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (
      this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]')
    );
  }

  get overflowListHovered() {
    return (
      this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false
    );
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());
    if (!(event.target instanceof Element) || !this.headerComponent) return;
    let item = findMenuItem(event.target);
    if (!item || item == this.#state.activeItem) return;
    const isDefaultSlot = event.target.slot === '';
    this.dataset.overflowExpanded = (!isDefaultSlot).toString();
    const previouslyActiveItem = this.#state.activeItem;
    if (previouslyActiveItem) previouslyActiveItem.ariaExpanded = 'false';
    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';
    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);
    if (!hasSubmenu && !isDefaultSlot) submenu = this.overflowMenu;
    if (submenu) {
      submenu.dataset.active = '';
      this.#cleanupMutationObserver();
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });
      setTimeout(() => this.#cleanupMutationObserver(), 500);
    }
    let finalHeight = submenu?.offsetHeight || 0;
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }
    if (!submenu) finalHeight = 0;
    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  };

  deactivate(event) {
    if (!(event.target instanceof Element)) return;
    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu      = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu       = event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu  = event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');
    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;
    this.#deactivate();
  }

  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;
    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';
    const submenu = findSubmenu(item);
    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    if (submenu) delete submenu.dataset.active;
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) cb(submenu);
      });
    };
    mapSubmenus((s) => s.style.setProperty('display', 'none'));
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((s) => s.style.removeProperty('display'));
    return height;
  }

  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;
    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');
    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;
    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);
    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

// ─────────────────────────────────────────────────────────────────────────────
// 工具函数
// ─────────────────────────────────────────────────────────────────────────────

function findMenuItem(element) {
  if (!(element instanceof Element)) return null;
  if (element?.matches('[slot="more"]')) {
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }
  return element?.querySelector('[ref="menuitem"]');
}

function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}

/**
 * 在多个 selector 中找第一个存在的元素
 * @param {ParentNode} root
 * @param {string[]} selectors
 * @returns {HTMLElement | null}
 */
function findFirst(root, selectors) {
  for (const selector of selectors) {
    const el = root.querySelector(selector);
    if (el instanceof HTMLElement) return el;
  }
  return null;
}

/**
 * 找到多个元素的最近公共祖先（LCA）
 * @param {...HTMLElement} elements
 * @returns {HTMLElement | null}
 */
function findLowestCommonAncestor(...elements) {
  if (elements.length === 0) return null;

  /**
   * 返回从 el 到根的祖先数组（含 el 自身），顺序从 el 到根
   * @param {HTMLElement} el
   * @returns {HTMLElement[]}
   */
  const getAncestors = (el) => {
    const list = [];
    let current = el;
    while (current instanceof HTMLElement) {
      list.push(current);
      current = current.parentElement;
    }
    return list;
  };

  // 从根到各元素的祖先集合（Set 用于 O(1) 查找）
  const ancestorSets = elements.map((el) => new Set(getAncestors(el)));

  // 沿第一个元素的祖先链，找第一个存在于所有其他集合里的节点
  const firstAncestors = getAncestors(elements[0]);
  for (const ancestor of firstAncestors) {
    if (ancestorSets.slice(1).every((set) => set.has(ancestor))) {
      return ancestor;
    }
  }

  return null;
}

/**
 * 找到 element 在 root 直接子级中的那个容器
 * @param {HTMLElement} root
 * @param {HTMLElement | null} element
 * @returns {HTMLElement | null}
 */
function getDirectChildContainer(root, element) {
  if (!(root instanceof HTMLElement) || !(element instanceof HTMLElement)) return element;
  if (element === root) return root;
  let current = element;
  while (current.parentElement && current.parentElement !== root) {
    current = current.parentElement;
  }
  return current;
}

/**
 * 批量设置 !important 样式
 * @param {HTMLElement} element
 * @param {Record<string, string>} styles
 */
function setImportantStyles(element, styles) {
  Object.entries(styles).forEach(([property, value]) => {
    element.style.setProperty(toKebabCase(property), value, 'important');
  });
}

/**
 * 批量清除样式
 * @param {HTMLElement} element
 * @param {string[]} properties
 */
function clearStyles(element, properties) {
  properties.forEach((property) => element.style.removeProperty(property));
}

/**
 * camelCase -> kebab-case
 * @param {string} value
 * @returns {string}
 */
function toKebabCase(value) {
  return value.replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`);
}